import { ReactNode, useEffect, useRef, useState } from 'react';
import { formatNumber, useHistory } from '../lib/actions';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import type { InteractionRow } from '../lib/types';
import { duration, Icon, type IconName } from './ui';

/** Who the open interaction is with. */
export interface Subject {
  interactionId: string;
  number: string;
  name: string | null;
  line: string;
}

type Tab = 'customer' | 'history' | 'notes' | 'crm';

const TABS: { id: Tab; label: string; icon: 'idcard' | 'history' | 'pen' | 'puzzle' }[] = [
  { id: 'customer', label: 'Customer', icon: 'idcard' },
  { id: 'history', label: 'History', icon: 'history' },
  { id: 'notes', label: 'Notes', icon: 'pen' },
  { id: 'crm', label: 'CRM', icon: 'puzzle' },
];

/**
 * Right-hand panel: the customer behind the open interaction. "CRM" embeds
 * the organization's own CRM (any URL template, the caller's number filled
 * in); "Notes" feed the wrap-up.
 */
export function ContextPanel({
  subject,
  notes,
  onNotes,
  mine,
}: {
  subject: Subject | null;
  notes: string;
  onNotes?: (v: string) => void;
  /** Everything the signed-in person has handled, newest first, for the History tab */
  mine: InteractionRow[] | null;
}) {
  const [tab, setTab] = useState<Tab>('customer');
  return (
    <aside className="flex min-h-[420px] min-w-0 flex-col bg-hull/30 xl:min-h-0" aria-label="Customer">
      <div role="tablist" className="flex h-14 shrink-0 items-center gap-1 border-b border-line px-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            title={t.label}
            aria-label={t.label}
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`grid h-9 w-9 place-items-center rounded-lg transition ${tab === t.id ? 'bg-ion/15 text-ion' : 'text-ink-3 hover:bg-veil/[0.06] hover:text-ink'}`}
          >
            <Icon name={t.icon} size={18} />
          </button>
        ))}
        <span className="ml-2 text-sm text-ink-2">{TABS.find((t) => t.id === tab)?.label}</span>
      </div>
      <div className="flex-1 overflow-auto">
        {tab === 'history' ? (
          <HistoryTab subject={subject} mine={mine} />
        ) : !subject && tab !== 'crm' ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center text-ink-3">
            <Icon name="user" size={28} />
            <p className="max-w-[240px] text-sm">Details of the caller appear here when a call rings or when you open a work item.</p>
          </div>
        ) : tab === 'customer' ? (
          <CustomerTab subject={subject!} />
        ) : tab === 'notes' ? (
          <div className="p-4">
            {onNotes ? (
              <label className="block">
                <span className="mb-2 block text-sm text-ink-2">Notes for this call. They go into the wrap-up.</span>
                <textarea
                  rows={14}
                  value={notes}
                  onChange={(e) => onNotes(e.target.value)}
                  className="w-full resize-y rounded-lg border border-line bg-abyss/60 px-3 py-2.5 text-sm outline-none focus:border-ion"
                />
              </label>
            ) : (
              <p className="whitespace-pre-wrap text-sm text-ink-2">{notes || 'No notes on this interaction.'}</p>
            )}
          </div>
        ) : (
          <CrmTab subject={subject} />
        )}
      </div>
    </aside>
  );
}

function Section({ title, children, defaultOpen = true }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-line">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold hover:bg-veil/[0.03]"
      >
        {title}
        <span className={`text-ink-3 transition ${open ? 'rotate-180' : ''}`}>
          <Icon name="chevronDown" size={15} />
        </span>
      </button>
      {open ? <div className="px-4 pb-4">{children}</div> : null}
    </section>
  );
}

function Fact({ icon, children, sub }: { icon: 'user' | 'phone' | 'idcard' | 'inbox' | 'history'; children: ReactNode; sub?: string }) {
  return (
    <li className="flex items-start gap-3 py-1.5 text-sm">
      <span className="mt-0.5 text-ink-3">
        <Icon name={icon} size={15} />
      </span>
      <span className="min-w-0">
        <span className="block truncate">{children}</span>
        {sub ? <span className="block text-xs text-ink-3">{sub}</span> : null}
      </span>
    </li>
  );
}

function CustomerTab({ subject }: { subject: Subject }) {
  const history = useHistory(subject.number || null);
  const previous = (history ?? []).filter((h) => h.id !== subject.interactionId);
  const last = previous[0];
  return (
    <>
      <Section title="Customer information">
        <ul>
          <Fact icon="user">{subject.name ?? 'Unknown caller'}</Fact>
          <Fact icon="phone">
            <span className="tabular">{formatNumber(subject.number)}</span>
          </Fact>
          <Fact icon="inbox" sub="Line">
            {subject.line}
          </Fact>
          <Fact icon="history" sub="Previous contacts">
            {history === null ? 'Checking…' : previous.length === 0 ? 'First contact' : `${previous.length}${previous.length >= 9 ? '+' : ''}`}
          </Fact>
        </ul>
      </Section>
      <CrmCard number={subject.number} />
      {last ? (
        <Section title="Last contact">
          <PastContact row={last} />
        </Section>
      ) : null}
    </>
  );
}

interface CrmRecord {
  configured: boolean;
  found?: boolean;
  name?: string | null;
  url?: string | null;
  fields?: { label: string; value: string }[];
  error?: string;
}

/** The caller as the organization's CRM knows them (the CRM lookup an admin sets up). */
function CrmCard({ number }: { number: string }) {
  const [rec, setRec] = useState<CrmRecord | null>(null);
  useEffect(() => {
    let live = true;
    setRec(null);
    const digits = number.replace(/[^\d+]/g, '');
    if (!/^\+?\d{3,16}$/.test(digits)) return;
    api<CrmRecord>('GET', `/crm/lookup?number=${encodeURIComponent(digits)}`)
      .then((r) => live && setRec(r))
      .catch(() => live && setRec({ configured: true, found: false, error: 'CRM lookup unavailable' }));
    return () => {
      live = false;
    };
  }, [number]);
  if (rec && !rec.configured) return null;
  return (
    <Section title="In your CRM">
      {rec === null ? (
        <p className="text-sm text-ink-3">Looking up…</p>
      ) : rec.error ? (
        <p className="text-sm text-ink-3">Could not reach the CRM: {rec.error}</p>
      ) : !rec.found ? (
        <p className="text-sm text-ink-2">No record for this number.</p>
      ) : (
        <div className="text-sm">
          {rec.name ? <p className="font-medium">{rec.name}</p> : null}
          {rec.fields?.length ? (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {rec.fields.map((f) => (
                <div key={f.label} className="contents">
                  <dt className="text-ink-3">{f.label}</dt>
                  <dd className="min-w-0 break-words">{f.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {rec.url ? (
            <a href={rec.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-ion hover:underline">
              Open the record in the CRM
            </a>
          ) : null}
        </div>
      )}
    </Section>
  );
}

/** How each channel shows in lists. Messaging joins as Nova gains it. */
export const CHANNELS: Record<string, { label: string; color: string; icon: IconName }> = {
  voice: { label: 'Voice', color: '#1f8f6b', icon: 'phone' },
  chat: { label: 'Livechat', color: '#f0901f', icon: 'chat' },
  email: { label: 'Email', color: '#3b82f6', icon: 'mail' },
  whatsapp: { label: 'WhatsApp', color: '#25d366', icon: 'inbox' },
  messenger: { label: 'Messenger', color: '#8b5cf6', icon: 'inbox' },
  instagram: { label: 'Instagram', color: '#e1306c', icon: 'inbox' },
};
export const channelOf = (c: string | undefined) => CHANNELS[c ?? 'voice'] ?? { label: c ?? 'Other', color: '#6b7873', icon: 'inbox' as const };

/**
 * Interaction history: everything you have handled, on every channel,
 * newest first; or, with a caller on screen, their earlier contacts.
 * Opening one shows it in the middle.
 */
function HistoryTab({ subject, mine }: { subject: Subject | null; mine: InteractionRow[] | null }) {
  const me = useNova((s) => s.user?.sub);
  const selectedId = useNova((s) => s.selectedId);
  const canNarrow = Boolean(subject?.number);
  const [view, setView] = useState<'mine' | 'caller'>('mine');
  const [query, setQuery] = useState('');
  const caller = useHistory(canNarrow && view === 'caller' ? subject!.number : null);
  const showing = view === 'caller' && canNarrow ? caller : mine;
  const needle = query.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const rows = (showing ?? [])
    .filter((h) => !(view === 'caller' && h.id === subject?.interactionId))
    .filter((h) => {
      if (!needle) return true;
      const name = (historyName(h, me) ?? '').toLowerCase();
      return name.includes(needle) || (digits !== '' && (h.customer_number ?? '').includes(digits));
    });

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2 border-b border-line p-3">
        {canNarrow ? (
          <div className="flex rounded-lg border border-line p-0.5 text-[12px]" role="group" aria-label="Whose history">
            {(
              [
                ['mine', 'Mine'],
                ['caller', 'This caller'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={view === id}
                onClick={() => setView(id)}
                className={`flex-1 rounded-md px-2 py-1 font-medium ${view === id ? 'bg-ion text-on-accent' : 'text-ink-2 hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or number"
          aria-label="Search history"
          className="w-full rounded-lg border border-line bg-abyss/50 px-3 py-1.5 text-sm outline-none focus:border-ion"
        />
      </div>
      {showing === null ? (
        <p className="p-4 text-sm text-ink-3">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="p-4 text-sm text-ink-2">
          {needle ? `Nothing matches "${query}".` : view === 'caller' && canNarrow ? 'No earlier contact with this caller.' : 'Interactions you handle appear here.'}
        </p>
      ) : (
        <ol aria-label={view === 'caller' ? 'Earlier contacts with this caller' : 'Your history'}>
          {rows.map((h) => {
            const ch = channelOf(h.channel);
            const missed = h.status === 'abandoned' || h.status === 'failed';
            return (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => useNova.getState().set({ selectedId: h.id })}
                  aria-current={h.id === selectedId ? 'true' : undefined}
                  className={`flex w-full items-start gap-3 border-b border-line/70 px-3 py-2.5 text-left transition ${h.id === selectedId ? 'bg-ion/[0.08]' : 'hover:bg-veil/[0.03]'}`}
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white" style={{ background: missed ? 'var(--color-break)' : ch.color }} title={ch.label}>
                    <Icon name={ch.icon} size={15} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">{historyName(h, me) ?? formatNumber(h.customer_number ?? 'Unknown')}</span>
                      <time className="tabular shrink-0 text-[11px] text-ink-3">{when(h.started_at)}</time>
                    </span>
                    <span className={`mt-0.5 block truncate text-[12px] ${missed ? 'text-break' : 'text-ink-3'}`}>
                      {ch.label} · {h.direction === 'internal' ? 'Internal' : h.direction === 'outbound' ? 'Outbound' : h.queue_name ?? 'Inbound'} ·{' '}
                      {h.disposition_label ?? (missed ? 'Not connected' : h.status === 'wrap_up' ? 'Needs wrap-up' : h.talk_seconds ? duration(h.talk_seconds) : 'Completed')}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Who a past interaction was with: the customer, or on an internal call the other colleague. */
function historyName(r: InteractionRow, me: string | undefined): string | null {
  if (r.direction !== 'internal') return r.customer_name;
  return r.agent_id === me ? r.peer_name : r.agent_name;
}

function when(iso: string): string {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function PastContact({ row }: { row: import('../lib/types').InteractionRow }) {
  return (
    <div className="text-sm">
      <div className="flex justify-between gap-2">
        <span>{new Date(row.started_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
        <span className="tabular text-ink-3">{row.talk_seconds ? duration(row.talk_seconds) : row.status}</span>
      </div>
      <p className="mt-1 text-ink-2">
        {row.disposition_label ?? 'No outcome'}
        {row.agent_name ? `, with ${row.agent_name}` : ''}
      </p>
      {row.notes ? <p className="mt-1 line-clamp-2 text-ink-3">{row.notes}</p> : null}
    </div>
  );
}

/** Events the embedded CRM page is told about, with window.postMessage. */
const CRM_EVENTS = ['call.answered', 'call.ended', 'interaction.dispositioned'];

function CrmTab({ subject }: { subject: Subject | null }) {
  const org = useNova((s) => s.org);
  const email = useNova((s) => s.email);
  const latest = useNova((s) => s.feed[0]);
  const frame = useRef<HTMLIFrameElement>(null);
  const template = org?.branding?.crmUrl;

  // Tell the CRM page what happens to this call, so it can open or update the record itself
  useEffect(() => {
    const target = frame.current?.contentWindow;
    if (!latest || !target || !template || !CRM_EVENTS.includes(latest.type)) return;
    if ((latest.data as { interactionId?: string }).interactionId !== subject?.interactionId) return;
    try {
      target.postMessage({ source: 'nova', type: latest.type, interactionId: subject?.interactionId, data: latest.data }, new URL(template).origin);
    } catch {
      /* a template that is not a full URL has no origin to post to */
    }
  }, [latest, subject?.interactionId, template]);

  if (!template) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p className="font-medium">No CRM connected</p>
        <p className="text-sm text-ink-2">
          An admin can embed your organization's CRM here by setting its URL, for example{' '}
          <code className="break-all rounded bg-hull px-1.5 py-0.5 text-xs">https://crm.example.com/search?phone={'{caller_number}'}</code>.
        </p>
      </div>
    );
  }
  const src = template
    .replace('{caller_number}', encodeURIComponent(subject?.number ?? ''))
    .replace('{interaction_id}', encodeURIComponent(subject?.interactionId ?? ''))
    .replace('{agent_email}', encodeURIComponent(email ?? ''));
  return (
    <div className="flex h-full min-h-[380px] flex-col">
      <iframe ref={frame} title="CRM" src={src} className="w-full flex-1 border-0 bg-white" sandbox="allow-scripts allow-forms allow-same-origin allow-popups" />
      <a className="block px-4 py-2 text-xs text-ink-3 hover:text-ink" href={src} target="_blank" rel="noreferrer">
        CRM blocks embedding? Open it in a new tab
      </a>
    </div>
  );
}
